
import Enumerable from "linq";
import { Component, Mixins, Prop, Watch, Emit } from "vue-property-decorator";
import { DomainRegistry } from "../../../../domain";
import {
  Document,
  DocumentRegistrationResult,
  CurrentEditors,
  IDocumentImageSource,
  DocumentTokenInformation,
  EditConfirmationActionKinds,
} from "../../../../domain/document";
import { IContentSource } from "../../../../domain/drawing";
import { ModelEditableMixin } from "../../../system/mixins";
import {
  EditingDocument,
  IRestrictions,
} from "../EditingDocument";
import { CommonComponentsAccessibleMixin } from "../../../document/editor/mixins";
import { IDocumentEditMenuConfiguration } from "../contents";

@Component
export default class DocumentEditorMixin extends Mixins(ModelEditableMixin, CommonComponentsAccessibleMixin) {
  @Prop({ default: false })
  shouldRestrictDocumentNote: boolean;

  @Prop()
  restrictions: IRestrictions;

  @Prop()
  editMenuConfiguration: IDocumentEditMenuConfiguration;

  @Prop()
  systemKey: string;

  @Prop()
  linkKey: string;

  @Prop({ default: null })
  initialize: (source: Document) => Promise<void>;

  @Prop()
  initializeNewExtraDocument: (extraDocument: Document, document: Document) => Promise<void>;

  @Prop()
  getExtraDocument: (patientId: string, formatId: number) => Promise<Document>;

  @Prop()
  register: (source: Document, isTemporary: boolean, isPublish: boolean) => Promise<DocumentRegistrationResult>;

  @Prop()
  cancel: (source: Document) => Promise<void>;

  @Prop()
  postRegistrationProcess: (id: number) => Promise<void>;

  @Prop()
  getReferenceFile: (fileId: number | string) => Promise<IContentSource>;

  @Prop()
  createImageSource: (imageText: string) => Promise<IDocumentImageSource>;

  @Prop()
  createPreviewCacheSource: (document: Document) => Promise<IContentSource>;

  @Prop()
  redirectUneditableDocument: (source: Document) => Promise<void>;

  @Prop()
  applyExternalRequestObjectValues: (document: EditingDocument) => Promise<void>;

  @Emit()
  documentRegistered(id: number, destinationRoute: any, isTemporary?: boolean, archiveId?: number, isWindowClosing?: boolean): any {}

  @Emit()
  documentReset(editToken: string, writeToken: string): void {}

  data(): any {
    return {
      document: this.value ? this.createNewEditingDocument(this.value) : null,
      isDocumentInitialized: false,
      confirmedEditTokens: [],
      confirmedCreateTokens: [],
      unconfirmedCreators: [],
      createToken: null as DocumentTokenInformation,
    };
  }
  document: EditingDocument;
  isDocumentInitialized: boolean;
  timer: any;
  confirmedEditTokens: string[];
  confirmedCreateTokens: string[];
  unconfirmedCreators: CurrentEditors[];
  createToken: DocumentTokenInformation;
  createTokenTimer: any;
  writeTokenTimer: any;

  mounted() {
    // TODO: refactor
    // cancelを引きまわしている意図が分からない
    if (this.cancel) {
      this.$registerCloseHandler(this.deleteEditToken);
    }
    this.$registerCloseHandler(this.deleteWriteToken);
  }

  destroyed() {
    // 文書登録後にPDF表示の画面に遷移したあともタイマーが発火してAPIを叩き続けてしまう現象への対策
    // 本来であれば、文書登録が完了した時点でタイマーを明示的にクリアしたほうがよい
    this.clearEditTokenTimer();
    this.clearWriteTokenTimer();
  }

  get shouldValidateAllPages(): boolean {
    return this.$configuration.behavior.document.editor.registerer.allPagesEditValidator.isEnable;
  }

  private createNewEditingDocument(source: Document): EditingDocument {
    const document = new EditingDocument(source, this.restrictions);
    document.injectInitializer(this.initialize);
    this.createToken = document.source.createToken;
    return document;
  }

  prohibitDropEvent(event: DragEvent): void {
    event.stopPropagation();
    event.preventDefault();
    event.dataTransfer.dropEffect = "none";
  }

  async deleteEditToken(): Promise<void> {
    this.clearEditTokenTimer();
    await this.cancel(this.editingSource);
  }

  private clearEditTokenTimer(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  async deleteWriteToken(): Promise<void> {
    this.clearWriteTokenTimer();
    await this.deleteDocumentWriteToken();
  }

  clearWriteTokenTimer(): void {
    if (!this.writeTokenTimer) { return; }
    clearInterval(this.writeTokenTimer);
    this.writeTokenTimer = null;
  }

  async extendTokenLifeSpan(): Promise<void> {
    // 延長機能の使用有無は呼び元のタイマーを起動させるかどうかで制御している
    const sourceDocument = this.editingSource as Document;
    if (!sourceDocument || !sourceDocument.editToken) { return; }
    const repository = DomainRegistry.document.getDocumentRepository();
    await repository.extendEditTokenLifeSpan(sourceDocument.editToken.token);
  }

  @Watch("editingSource")
  async onEditingSourceUpdated(newValue: Document, oldValue: Document) {
    try {
      this.$logger.debug("onEditingSourceUpdated");
      console.debug(newValue);
      await this._onEditingSourceUpdated(newValue, oldValue);
    } finally {
      this.$logger.debug("onEditingSourceUpdated is finished.(initialized: " + (this.document ? this.document.isAlreadyInitialized : "invalid") + ")");
    }
  }

  private async _onEditingSourceUpdated(newValue: Document, oldValue: Document): Promise<void> {
    if ((this.document !== null) && (this.document.source === newValue)) {
      return;
    }
    if (newValue && newValue.writeToken && newValue.writeToken.tokenLifeSpan > 0 && !this.writeTokenTimer) {
      this.$logger.info("文書記載トークンを延長する定期実行タイマーを開始します。");
      this.writeTokenTimer = setInterval(this.extendDocumentWriteTokenLifeSpan, this.intervalToExtend(newValue.writeToken.tokenLifeSpan));
    }
    const conflictConfig = this.$configuration.behavior.document.editor.conflict;
    if (!this.timer && conflictConfig.editTokenManagement.isEnable && newValue && newValue.documentId && newValue.editToken && newValue.editToken.token && newValue.editToken.tokenLifeSpan > 0) {
      this.$logger.info("文書編集トークンを延長する定期実行タイマーを開始します。");
      this.timer = setInterval(this.extendTokenLifeSpan, this.intervalToExtend(newValue.editToken.tokenLifeSpan));
    }
    const currentEditors = newValue.documentId && newValue.editToken ? newValue.editToken.editors : null;
    const unconfirmedEditors = conflictConfig.editConfirmation.isEnable
      ? this.extractUnconfirmedEditors(currentEditors, this.confirmedEditTokens)
      : [];
    if (0 < unconfirmedEditors.length) {
      const result = await this.confirmToBegin(unconfirmedEditors);
      const token = newValue.documentId && newValue.editToken ? newValue.editToken.token : "";
      if (result) {
        this.addConfirmedEditors(unconfirmedEditors);
        this.registerDocumentLockBreakSystemLog(newValue);

        const writeToken = newValue.writeToken ? newValue.writeToken.token : null;
        this.documentReset(token, writeToken);
      } else {
        this.$closeWindow(true);
      }
      return;
    }
    const document = newValue
      ? (this.document ? this.document.clone(newValue) : this.createNewEditingDocument(newValue))
      : null;
    const isDocumentAlreadyInitialized = this.isDocumentInitialized;
    if (!this.isDocumentInitialized && document) {
      try {
        if (this.createToken && this.createToken.token && !this.$commonDialogs.document.editConfirmation.isVisible) {
          if (!this.createTokenTimer && conflictConfig.createTokenManagement.isEnable && this.createToken.tokenLifeSpan > 0) {
            this.$logger.info("文書作成トークンを延長する定期実行タイマーを開始します。");
            this.createTokenTimer = setInterval(this.extendDocumentCreateTokenLifeSpan, this.intervalToExtend(this.createToken.tokenLifeSpan));
          }
          const currentCreators = newValue.createToken ? newValue.createToken.editors : null;
          this.unconfirmedCreators = conflictConfig.createConfirmation.isEnable
            ? this.extractUnconfirmedEditors(currentCreators, this.confirmedCreateTokens)
            : [];
          if (this.unconfirmedCreators.length > 0) {
            await this.confirmDuplicatedCreators(newValue);
          }
        }
        if (newValue.attributes.isFixed) {
          await this.showFixedDocumentAlert();
          this.$closeWindow(true);
          return;
        }
        if (newValue.attributes.isVirtual) {
          await this.showVirtualDocumentAlert();
          this.$closeWindow(true);
          return;
        }
        await document.initialize();
        document.restrictions = this.restrictions; // 初期化中に制約が更新される可能性があるのでdocumentに反映する前に現状の制約を設定する.
        if (this.applyExternalRequestObjectValues) {
          await this.applyExternalRequestObjectValues(document);
        }
        this.isDocumentInitialized = true;
      } catch (e) {
        this.$error("エラー", e.message, e);
      }
    }
    this.document = document; // this.documentの更新は最後に1回だけ.(初期化未完了の中途半端な状態で処理が進むのを回避.)
    if (!isDocumentAlreadyInitialized && this.isDocumentInitialized) {
      this.$logger.info("文書初期化完了イベント発行.");
      this.initialized();     // 初期化完了イベントは初期化完了後にdocumentを設定してからemitする.
    }
  }

  /**
   * 文書編集の競合を突破したときのシステムログを追加する
   * （登録の完了は待たない）
   */
  private registerDocumentLockBreakSystemLog(document: Document) {
    try {
      const message = "編集中ロックを突破して文書編集を開始しました。";
      const repo = DomainRegistry.document.getDocumentRepository();
      repo.addDocumentOperationLog(document.attributes.patient.id, document.documentId, message)
      .catch(error => {
        this.$logger.error(error);
      });
    } catch (e) {
      this.$logger.error(e);
    }
  }

  private extractUnconfirmedEditors(editors: CurrentEditors[], confirmedTokens: string[]): CurrentEditors[] {
    return !editors
      ? []
      : Enumerable.from(editors).where(e => this.isUnconfirmedToken(e, confirmedTokens)).toArray();
  }

  private isUnconfirmedToken(editor: CurrentEditors, confirmedTokens: string[]): boolean {
    return !Enumerable.from(confirmedTokens).contains(editor.token);
  }

  private addConfirmedEditors(editors: CurrentEditors[]): void {
    this.confirmedEditTokens = this.confirmedEditTokens.concat(Enumerable.from(editors).select(e => e.token).toArray());
  }

  private addConfirmedCreators(editors: CurrentEditors[]): void {
    this.confirmedCreateTokens = this.confirmedCreateTokens.concat(Enumerable.from(editors).select(e => e.token).toArray());
  }

  private async confirmToBegin(currentEditors: CurrentEditors[], isNewEntry: boolean = false): Promise<boolean> {
    const actionKind = isNewEntry ? EditConfirmationActionKinds.Create : EditConfirmationActionKinds.Edit;
    const isSingleEntryFormat = await this.isSingleEntryFormat(); // ダイアログを開くたびに取得することになるが、そもそも開く頻度が少ないのでここで取得している
    return this.$commonDialogs.document.editConfirmation.show(currentEditors, actionKind, !isSingleEntryFormat);
  }

  private async isSingleEntryFormat(): Promise<boolean> {
    if (!this.$configuration.behavior.document.editor.singleEntry.isEnable) { return false; }
    const repo = DomainRegistry.format.getFormatRepository();
    return repo.isSingleEntry(this.editingSource.attributes.format.originalId);
  }

  private async confirmDuplicatedCreators(newValue: Document): Promise<void> {
    const result = await this.confirmToBegin(this.unconfirmedCreators, true);
    if (result) {
      this.addConfirmedCreators(this.unconfirmedCreators);
      const creatorsWithoutMyself = await this.getDocumentCurrentEditors(this.systemKey, this.linkKey, newValue.attributes.patient.id, newValue.createToken.token);
      this.unconfirmedCreators = this.extractUnconfirmedEditors(creatorsWithoutMyself, this.confirmedCreateTokens);
    } else {
      await this.$closeWindow(true);
    }
    if (this.unconfirmedCreators.length > 0) { await this.confirmDuplicatedCreators(newValue); }
  }

  private async showFixedDocumentAlert(): Promise<void> {
    // ワークフロー文書・依頼書は編集不可の指定ができないようにガードしているので、
    // 直接記載の文書でしか表示されないはず
    // 編集中に編集不可の指定がされた場合は、登録時にもチェックするので新規文書として登録する流れになる（ワークフロー文書はチェックしていない）
    const messages = ["文書が編集不可に指定されています。", "編集を続行することが出来ません。"];
    await this.$commonDialogs.confirmation.show({
      title: "編集不可",
      message: messages,
      buttons: [],
      cancellation: {
        isEnable: true,
        label: "閉じる"
      },
    });
  }

  private async showVirtualDocumentAlert(): Promise<void> {
    const messages = ["仮想文書です。", "編集を続行することが出来ません。"];
    await this.$commonDialogs.confirmation.show({
      title: "編集不可",
      message: messages,
      buttons: [],
      cancellation: {
        isEnable: true,
        label: "閉じる"
      },
    });
  }

  @Watch("restrictions")
  onRestrictionsUpdated(newValue: IRestrictions, oldValue: IRestrictions) {
    this.$logger.debug("onRestrictionsUpdated");
    console.debug(newValue);
    if (this.document === null) {
      return;
    }
    this.document = this.document.clone(this.document.source, newValue);
  }

  @Emit()
  initialized(): void {}

  updateEditingDocument(delegate: (editingDocument: EditingDocument) => void): void {
    if (!this.isDocumentInitialized) {
      this.$warn("初期化", "文書の初期化が完了していません。");
      return;
    }
    const cloned = this.document.clone();
    delegate(cloned);
    this.document = cloned;
    this.$nextTick(() => {
      this.document.executeStackedScriptsAsync();
    });
  }

  async registerDocument(isTemporary: boolean): Promise<number> {
    if (isTemporary) {
      return this.commonComponents.operators.registerer.registerDocumentAsTemporary(this.shouldRestrictDocumentNote, this.shouldValidate);
    } else {
      return this.commonComponents.operators.registerer.registerDocument(this.shouldValidate);
    }
  }

  async deleteDocumentCreateToken(): Promise<void> {
    if (!this.createToken) { return; }
    const repository = DomainRegistry.document.getDocumentRepository();
    await repository.deleteDocumentCreateToken(this.createToken.token);
  }

  async extendDocumentCreateTokenLifeSpan(): Promise<void> {
    if (!this.createToken) { return; }
    const repository = DomainRegistry.document.getDocumentRepository();
    await repository.extendDocumentCreateTokenLifeSpan(this.createToken.token);
  }

  intervalToExtend(lifeSpanSeconds: number): number {
    return Math.max(Math.floor(lifeSpanSeconds / 3) * 1000, 10000);  // registriesに指定した寿命の３分の１を返す(最短10秒間隔)
  }

  get writeToken(): DocumentTokenInformation {
    return this.readPropertyOrDefault((d: Document) => d.writeToken, null);
  }

  async deleteDocumentWriteToken(): Promise<void> {
    if (!this.writeToken || !this.writeToken.token) { return; }
    const repository = DomainRegistry.document.getDocumentRepository();
    await repository.deleteDocumentWriteToken(this.writeToken.token);
  }

  async extendDocumentWriteTokenLifeSpan(): Promise<void> {
    if (!this.writeToken || !this.writeToken.token) { return; }
    const repository = DomainRegistry.document.getDocumentRepository();
    await repository.extendWriteTokenLifeSpan(this.writeToken.token);
  }

  async getDocumentCurrentEditors(systemKey: string, linkKey: string, patientCode: string, exclusionToken: string): Promise<CurrentEditors[]> {
    if (!systemKey && !linkKey) {
      // TODO: fix
      // DMMでの重複記載対策のためにmst.RegistriesでShouldDetectSameFormatDocumentCreationをtrueに設定したとき、
      // ゆるロックの突破でsystemKey・linkKeyが空でここに到達することになる
      // 再チェックできたほうが良いが、エラー通知が出てもどうしようもないので、ガードを追加している
      this.$logger.warn("systemKey・linkKeyが空の場合の新規文書作成トークン取得は未実装です。");
      return [];
    }
    try {
      const repository = DomainRegistry.collaboration.getLinkedDocumentRepository();
      const editors = await repository.getDocumentCurrentEditors(systemKey, linkKey, patientCode, exclusionToken);
      return editors;
    } catch (e) {
      this.$error("連携文書作成トークン取得失敗", "連携文書作成トークンを取得できませんでした。", e);
      return null;
    }
  }

  onDocumentEditorContentsClicked(): void {
    if (!!this.document && !!this.document.attributes && !!this.document.attributes.editOperation && !this.document.attributes.editOperation.firstClickDateTime) {
      this.$logger.debug("記入開始日時を取得しました。");
      // 特にリアクティブにする必要は無いので直接格納している
      // editOperationはDocumentAttributesをCloneしても同じインスタンスなので、更新タイミングが競合したとしても問題ないはず
      this.document.attributes.editOperation.firstClickDateTime = new Date();
    }
  }
}
